import React from 'react';
import { Star } from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'motion/react';

interface AnimaTicketIconProps {
  stars: number;
  coreStars?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export default function AnimaTicketIcon({ stars, coreStars, size = 'md', className }: AnimaTicketIconProps) {
  const renderedCoreStars = coreStars ?? stars;
  // Base border/corner/padding classes
  const baseSizeClasses = {
    xs: 'rounded-lg border-2',
    sm: 'rounded-xl border-2',
    md: 'rounded-2xl border-2',
    lg: 'rounded-2xl border-[3px]',
    xl: 'rounded-3xl border-4',
  };

  // Dimensions to apply only if not overridden by className
  const defaultDimensions = {
    xs: 'w-7 h-7 py-0.5 px-0.5',
    sm: 'w-11 h-11 py-1 px-1',
    md: 'w-16 h-16 py-1.5 px-1.5',
    lg: 'w-20 h-20 py-2 px-2',
    xl: 'w-24 h-24 py-3 px-3',
  };

  const sphereSizes = {
    xs: 'w-3.5 h-3.5 shadow-sm',
    sm: 'w-6 h-6 shadow-sm',
    md: 'w-9 h-9 shadow-md',
    lg: 'w-12 h-12 shadow-lg',
    xl: 'w-15 h-15 shadow-xl',
  };

  const starSizes = {
    xs: 'w-1 h-1',
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
    xl: 'w-5 h-5',
  };

  const gapClasses = {
    xs: 'gap-0.5',
    sm: 'gap-1',
    md: 'gap-1.5',
    lg: 'gap-2',
    xl: 'gap-2.5',
  };

  const tierClasses: Record<number, string> = {
    1: 'bg-gradient-to-br from-[#a75d28] via-[#8c4516] to-[#5c2a0b] border-[#c27a4a]', // Bronze
    2: 'bg-gradient-to-br from-[#cbd5e1] via-[#94a3b8] to-[#475569] border-slate-300', // Silver
    3: 'bg-gradient-to-br from-[#fbbf24] via-[#d97706] to-[#78350f] border-yellow-300', // Gold
  };

  const hasCustomDimensions = className && (className.includes('w-') || className.includes('h-'));

  return (
    <div 
      className={cn(
        "relative flex flex-col items-center justify-center shadow-md select-none shrink-0 overflow-hidden",
        tierClasses[stars] || tierClasses[1],
        baseSizeClasses[size],
        !hasCustomDimensions && defaultDimensions[size],
        gapClasses[size],
        className
      )}
      style={{
        // Give it a subtle ticket cut-out notch on left and right sides
        clipPath: 'polygon(0% 0%, 100% 0%, 100% 40%, 92% 45%, 92% 55%, 100% 60%, 100% 100%, 0% 100%, 0% 60%, 8% 55%, 8% 45%, 0% 40%)'
      }}
    >
      
      {/* White Sphere */}
      <div 
        className={cn(
          "rounded-full bg-gradient-to-br from-white via-slate-100 to-slate-300 border border-white/60 flex items-center justify-center relative z-10 overflow-hidden",
          sphereSizes[size]
        )}
        style={{
          boxShadow: 'inset -1.5px -1.5px 3.5px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.15)'
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={renderedCoreStars}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 1.5, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex items-center justify-center w-full h-full"
          >
            {/* Yellow Stars Inside Sphere */}
            {renderedCoreStars === 3 ? (
              <div className="flex flex-col items-center justify-center leading-none">
                {/* Top row: 2 stars */}
                <div className={cn("flex justify-center", gapClasses[size])}>
                  <Star 
                    className={cn("fill-yellow-400 text-yellow-400 drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)] animate-pulse", starSizes[size])}
                    style={{ animationDelay: '0s' }}
                  />
                  <Star 
                    className={cn("fill-yellow-400 text-yellow-400 drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)] animate-pulse", starSizes[size])}
                    style={{ animationDelay: '0.15s' }}
                  />
                </div>
                {/* Bottom row: 1 star */}
                <div 
                  className="flex justify-center"
                  style={{ 
                    marginTop: size === 'xs' ? '-2px' : size === 'sm' ? '-3px' : size === 'md' ? '-4px' : size === 'lg' ? '-6px' : '-8px' 
                  }}
                >
                  <Star 
                    className={cn("fill-yellow-400 text-yellow-400 drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)] animate-pulse", starSizes[size])}
                    style={{ animationDelay: '0.3s' }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex justify-center gap-0.5">
                {Array.from({ length: renderedCoreStars }).map((_, i) => (
                  <Star 
                    key={i} 
                    className={cn("fill-yellow-400 text-yellow-400 drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)] animate-pulse", starSizes[size])}
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
